// src/features/inventory/pages/CreateSupplierPage.jsx
import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  TextField,
} from '@mui/material';
import {
  Save as SaveIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNavigationStack } from '../../../context/NavigationStackContext';
import { useSuppliers } from '../hooks/useSuppliers';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { getCreatedId } from '../../../utils/helpers/getCreatedId';
import { FormPage, FormSection, FormRow, FormActions } from '../../../components/common/form';

const EMPTY_ADDRESS = {
  address_line1: '',
  address_line2: '',
  postal_code: '',
  city: '',
  country: 'Denmark',
};

const EMPTY_FORM = {
  code: '',
  name: '',
  contact_email: '',
  contact_phone: '',
  address: { ...EMPTY_ADDRESS },
};

export default function CreateSupplierPage() {
  const { t } = useTranslation(['inventory', 'common']);
  const navigate = useNavigate();
  const { navigateBack } = useNavigationStack();
  const { showSuccess, showError } = useSnackbar();

  const { loading, error, clearError, createSupplier } = useSuppliers();

  const [values, setValues] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});

  const handleChange = useCallback((field, value) => {
    setValues(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const handleAddressChange = useCallback((field, value) => {
    setValues(prev => ({ ...prev, address: { ...prev.address, [field]: value } }));
    setErrors(prev => ({ ...prev, [field]: null }));
  }, []);

  const handleSubmit = async () => {
    const newErrors = {};
    if (!values.code.trim()) newErrors.code = t('inventory:suppliers.validation.code_required');
    if (!values.name.trim()) newErrors.name = t('inventory:suppliers.validation.name_required');

    // Country is pre-filled, so it alone doesn't mean an address was entered
    const hasAddressData = Object.entries(values.address).some(([key, v]) => key !== 'country' && v?.trim());
    if (hasAddressData) {
      if (!values.address.address_line1.trim()) newErrors.address_line1 = t('inventory:suppliers.validation.address_line1_required');
      if (!values.address.postal_code.trim()) newErrors.postal_code = t('inventory:suppliers.validation.postal_code_required');
      if (!values.address.city.trim()) newErrors.city = t('inventory:suppliers.validation.city_required');
      if (!values.address.country.trim()) newErrors.country = t('inventory:suppliers.validation.country_required');
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    const addressPayload = hasAddressData ? {
      address_line1: values.address.address_line1.trim(),
      address_line2: values.address.address_line2.trim() || null,
      postal_code: values.address.postal_code.trim(),
      city: values.address.city.trim(),
      country: values.address.country.trim(),
    } : null;

    const result = await createSupplier({
      code: values.code.trim(),
      name: values.name.trim(),
      contact_email: values.contact_email.trim() || null,
      contact_phone: values.contact_phone.trim() || null,
      address: addressPayload,
    });

    if (result.success) {
      showSuccess(t('inventory:suppliers.success.created'));
      // Open the new record (replace, so Back skips the filled-in form)
      const newId = getCreatedId(result, 'supplier_id');
      navigate(newId ? `../${newId}` : '..', { replace: true });
    } else {
      showError(result.error);
    }
  };

  const handleCancel = () => navigateBack('/inventory/suppliers');

  return (
    <FormPage
      title={t('inventory:suppliers.create_form.title')}
      subtitle={t('inventory:suppliers.create_form.subtitle')}
      onBack={handleCancel}
      onSubmit={handleSubmit}
      fieldErrors={errors}
      error={error}
      onErrorClose={clearError}
    >
      <FormSection>
        <FormRow widths={[1, 3]} minWidth={160}>
          <TextField
            label={t('inventory:suppliers.create_form.code')}
            value={values.code}
            onChange={(e) => handleChange('code', e.target.value)}
            fullWidth
            required
            error={Boolean(errors.code)}
            helperText={errors.code}
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
          <TextField
            label={t('inventory:suppliers.create_form.name')}
            value={values.name}
            onChange={(e) => handleChange('name', e.target.value)}
            fullWidth
            required
            error={Boolean(errors.name)}
            helperText={errors.name}
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
        </FormRow>
        <FormRow>
          <TextField
            label={t('inventory:suppliers.create_form.email')}
            value={values.contact_email}
            onChange={(e) => handleChange('contact_email', e.target.value)}
            fullWidth
            type="email"
            slotProps={{ htmlInput: { maxLength: 255 } }}
          />
          <TextField
            label={t('inventory:suppliers.create_form.phone')}
            value={values.contact_phone}
            onChange={(e) => handleChange('contact_phone', e.target.value)}
            fullWidth
            slotProps={{ htmlInput: { maxLength: 50 } }}
          />
        </FormRow>
      </FormSection>

      <FormSection title={t('inventory:suppliers.create_form.address_section')}>
        <TextField
          label={t('inventory:suppliers.create_form.address_line1')}
          value={values.address.address_line1}
          onChange={(e) => handleAddressChange('address_line1', e.target.value)}
          fullWidth
          error={Boolean(errors.address_line1)}
          helperText={errors.address_line1}
          slotProps={{ htmlInput: { maxLength: 255 } }}
        />
        <TextField
          label={t('inventory:suppliers.create_form.address_line2')}
          value={values.address.address_line2}
          onChange={(e) => handleAddressChange('address_line2', e.target.value)}
          fullWidth
          slotProps={{ htmlInput: { maxLength: 255 } }}
        />
        <FormRow widths={[1, 3]} minWidth={140}>
          <TextField
            label={t('inventory:suppliers.create_form.postal_code')}
            value={values.address.postal_code}
            onChange={(e) => handleAddressChange('postal_code', e.target.value)}
            fullWidth
            error={Boolean(errors.postal_code)}
            helperText={errors.postal_code}
            slotProps={{ htmlInput: { maxLength: 20 } }}
          />
          <TextField
            label={t('inventory:suppliers.create_form.city')}
            value={values.address.city}
            onChange={(e) => handleAddressChange('city', e.target.value)}
            fullWidth
            error={Boolean(errors.city)}
            helperText={errors.city}
            slotProps={{ htmlInput: { maxLength: 100 } }}
          />
        </FormRow>
        <TextField
          label={t('inventory:suppliers.create_form.country')}
          value={values.address.country}
          onChange={(e) => handleAddressChange('country', e.target.value)}
          fullWidth
          error={Boolean(errors.country)}
          helperText={errors.country}
          slotProps={{ htmlInput: { maxLength: 100 } }}
        />
      </FormSection>

      <FormActions
        onCancel={handleCancel}
        submitType="submit"
        submitLabel={t('common:buttons.create')}
        submitIcon={<SaveIcon />}
        loading={loading}
      />
    </FormPage>
  );
}
